import { useState } from "react";
import { Search, Loader2, Eye, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { Facility } from "@shared/schema";
import { format } from "date-fns";

const STATUS_COLORS: Record<string, string> = {
  indicative: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  submitted: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  settled: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  declined: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  withdrawn: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  scenario_not_proceeding: "bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
};

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return format(new Date(value), "dd MMM yyyy");
}

export default function FacilityList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [, navigate] = useLocation();

  const { data: facilities = [], isLoading, isError, error } = useQuery<Facility[]>({
    queryKey: ["/api/crm/facilities"],
  });

  // Filter facilities
  const filteredFacilities = facilities.filter((facility) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        facility.facilityName.toLowerCase().includes(searchLower) ||
        facility.facilityType?.toLowerCase().includes(searchLower) ||
        facility.facilityStatus?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    return true;
  });

  const handleViewFacility = (facilityId: string) => {
    navigate(`/facilities/${facilityId}`);
  };

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Facilities"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Facilities" }]}
        actions={
          <div className="flex gap-2">
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search facilities..."
                className="pl-8 pr-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="input-search-facilities"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Clear search"
                  data-testid="button-clear-search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        }
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-4">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <Card className="p-8 text-center">
            <p className="text-destructive font-medium">Failed to load facilities</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error instanceof Error ? error.message : "An error occurred"}
            </p>
          </Card>
        ) : filteredFacilities.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">
              {searchTerm ? "No facilities match your search" : "No facilities found"}
            </p>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Facility Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Requested</TableHead>
                  <TableHead className="text-right">Approved</TableHead>
                  <TableHead className="text-right">Settled</TableHead>
                  <TableHead>Application Date</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFacilities.map((facility) => (
                  <TableRow key={facility.id}>
                    <TableCell className="font-medium">
                      <Link href={`/facilities/${facility.id}`} className="hover:underline">
                        {facility.facilityName}
                      </Link>
                    </TableCell>
                    <TableCell>{facility.facilityType || "-"}</TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={`capitalize ${
                          STATUS_COLORS[facility.facilityStatus || "indicative"]
                        }`}
                      >
                        {(facility.facilityStatus || "indicative").replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(facility.requestedAmount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(facility.approvedLimit)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(facility.fundedAmount)}
                    </TableCell>
                    <TableCell>{formatDate(facility.applicationDate)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewFacility(facility.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </main>
    </div>
  );
}
